import React, { useEffect, useState } from "react";
import { Input, Space, Select, Table, Tooltip, Pagination, Modal, Form, Dropdown, Tabs } from "antd";
import { StopOutlined, CheckCircleOutlined, UserSwitchOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { FiSearch, FiDownload, FiPlus, FiFileText, FiMoreVertical } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { EncabezadoAdmin, ChipEstado, FiltroChips, Iniciales, claseBtnPrimario, claseBtnSecundario } from "../../../Ui/Admin/PiezasAdmin";
import { formatearNombre } from "../../../../utils/texto";
import { exportarHojaUnica } from "../../../../utils/exportExcel";
import { COMUNAS_RM } from "../../../../utils/comunas";
import Usuarios from "../../../../service/Usuarios.js";
import Recintos from "../../../../service/Recintos.js";
import Familiares from "../../../../service/Familiares.js";
import { useWithNotify, useNotify } from "../../../../providers/NotificationProvider.jsx";
import useAuthContext from "../../../../hooks/useAuthContext.js";
import { App } from "antd";
import FichaUsuarioDrawer from "./FichaUsuarioDrawer.jsx";
import Roles from "../../../../service/Roles.js";


dayjs.extend(relativeTime);
dayjs.locale("es");

const DEBOUNCE_MS = 350;


// Los roles ya NO están escritos acá: se piden a la API (`/roles/asignables`), que
// es la única forma de que un rol creado desde el panel aparezca en el selector sin
// recompilar el frontend. Estas constantes quedaron como respaldo mientras carga.
const ROLES_OPTIONS_BASE = [{ label: "Todos los roles", value: "" }];







const COMUNAS_OPTIONS = COMUNAS_RM.map(c => ({ label: c, value: c }));


// Valores en minúscula, igual que los guarda el registro: con "Masculino" el selector
// no reconocía el dato guardado y mostraba el texto crudo.
const GENDER_OPTIONS = [
    { label: "Masculino", value: "masculino" },
    { label: "Femenino", value: "femenino" },
    { label: "Otro", value: "otro" },
];


export default function UsuariosList() {
    const [loading, setLoading] = useState(false);
    const [exportandoCSV, setExportandoCSV] = useState(false); // 🆕
    const withNotify = useWithNotify();
    const notify = useNotify();
    const { modal } = App.useApp();
    const { user } = useAuthContext();
    const canManageUsers = user?.isAdmin || user?.isCoordinador;

    const navigate = useNavigate();

    // Filtros. `qInput` es lo escrito; `q` lo consultado. Antes se pedía una página a
    // la API por cada tecla (sobre 25 mil usuarios); ahora espera a que dejes de escribir.
    const [qInput, setQInput] = useState("");
    const [q, setQ] = useState("");
    const [rol, setRol] = useState("");
    const [estado, setEstado] = useState("");


    // Datos de la página actual
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);

    // Paginación
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);


    // Modal de edición de rol
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm] = Form.useForm();
    const [editLoading, setEditLoading] = useState(false);


    // Modal de edición completa
    const [editFullModalVisible, setEditFullModalVisible] = useState(false);
    const [editFullForm] = Form.useForm();
    const [editFullLoading, setEditFullLoading] = useState(false);
    const [tabEdicion, setTabEdicion] = useState("datos");

    // Familiares del usuario en edición
    const [familiares, setFamiliares] = useState([]);
    const [familiaresLoading, setFamiliaresLoading] = useState(false);


    // Recintos
    const [recintos, setRecintos] = useState([]);
    const [loadingRecintos, setLoadingRecintos] = useState(false);

    // Ficha de usuario
    const [fichaUsuario, setFichaUsuario] = useState(null);

    // Roles asignables, traídos de la API. Es lo que hace que un rol creado desde
    // «Roles y permisos» aparezca acá sin volver a compilar el frontend.
    const [rolesDisponibles, setRolesDisponibles] = useState([]);

    useEffect(() => {
        Roles.asignables()
            .then(setRolesDisponibles)
            .catch(err => {
                console.error('No se pudieron cargar los roles asignables:', err);
                setRolesDisponibles([]);
            });
    }, []);

    // `cliente` antes no se ofrecía, pero esta lista incluye a todos los vecinos: al
    // editar uno, su rol no calzaba con ninguna opción, y a alguien del equipo no había
    // cómo devolverlo a vecino. Ahora va primero y con su propia etiqueta.
    const rolesAdministrativos = rolesDisponibles.filter(r => r.key !== 'cliente');
    const nombreCliente = rolesDisponibles.find(r => r.key === 'cliente')?.nombre || 'Vecino';
    const OPCION_CLIENTE = {
        label: `${nombreCliente} (sin acceso al panel)`,
        value: 'cliente',
    };
    const ROLES_OPTIONS = [
        ...ROLES_OPTIONS_BASE,
        { label: `${nombreCliente}s`, value: 'cliente' },
        ...rolesAdministrativos.map(r => ({ label: r.nombre, value: r.key })),
    ];
    const ROLES_EDIT_OPTIONS = [
        OPCION_CLIENTE,
        ...rolesAdministrativos.map(r => ({
            // Que un rol no entre al panel es información útil justo acá: es el caso
            // de `funcionario`, que existe solo para abrir el torniquete.
            label: r.entraAlPanel === false ? `${r.nombre} (sin acceso al panel)` : r.nombre,
            value: r.key,
        })),
    ];
    const NOMBRES_ROL = Object.fromEntries(rolesDisponibles.map(r => [r.key, r.nombre]));


    // Watch del campo role en los formularios
    const selectedRole = Form.useWatch('role', editForm);
    const selectedFullRole = Form.useWatch('role', editFullForm);


    const load = async () => {
        try {
            setLoading(true);
            const params = { page, limit };
            if (rol) params.role = rol;
            if (estado) params.isActive = estado;
            if (q.trim()) params.search = q.trim();

            const res = await Usuarios.List(params);
            setUsers(res.data.users || []);
            setTotalUsers(res.data.pagination?.totalUsers || 0);
        } catch (e) {
            notify.error(e.message || "No se pudieron cargar los usuarios");
            setUsers([]);
            setTotalUsers(0);
        } finally {
            setLoading(false);
        }
    };


    const loadRecintos = async () => {
        try {
            setLoadingRecintos(true);
            const res = await Recintos.List({ limit: 0, estado: "activo" });
            const recintosOptions = res.data.map(r => ({
                label: r.name || r.nombre + (r.comuna ? ` - ${r.comuna}` : ""),
                value: r.nombre,
            }));
            setRecintos(recintosOptions);
        } catch (e) {
            notify.error(e.message || "No se pudieron cargar los recintos");
            setRecintos([]);
        } finally {
            setLoadingRecintos(false);
        }
    };


    useEffect(() => { loadRecintos(); }, []);
    useEffect(() => { load(); }, [page, limit, rol, estado, q]);


    const handleSearch = (value) => { setQInput(value); setQ(value); setPage(1); };

    useEffect(() => {
        if (qInput === q) return;
        const t = setTimeout(() => { setQ(qInput); setPage(1); }, DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [qInput, q]);

    const handleClearFilters = () => { setQInput(""); setQ(""); setRol(""); setEstado(""); setPage(1); };


    // Exportar Excel respetando los filtros activos. Antes bajaba el CSV crudo de
    // la API: sin encabezado, con "true"/"false", la key del rol y fechas ISO en UTC.
    const handleExportarCSV = async () => {
        setExportandoCSV(true);
        try {
            const usuarios = await Usuarios.ObtenerParaExportar({
                role: rol || undefined,
                isActive: estado || undefined,
                search: q.trim() || undefined
            });

            // Excel no tiene zona horaria: se le pasa la hora de reloj de Chile,
            // si no los registros de la noche aparecen al día siguiente.
            const horaLocal = (iso) => {
                const d = iso ? new Date(iso) : null;
                return d && !Number.isNaN(d.getTime()) ? new Date(d.getTime() - d.getTimezoneOffset() * 60000) : null;
            };
            const capitalizar = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');
            const nombreRol = (key) => NOMBRES_ROL[key] || (key && key !== 'cliente' ? key : nombreCliente);

            const columnas = [
                { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 34, valor: u => formatearNombre(u['Nombre']) },
                { header: 'RUT', key: 'rut', tipo: 'texto', valor: u => u['RUT'] },
                { header: 'Email', key: 'email', tipo: 'texto', ancho: 34, valor: u => u['Email'] },
                { header: 'Teléfono', key: 'telefono', tipo: 'texto', valor: u => u['Teléfono'] },
                { header: 'Rol', key: 'rol', tipo: 'texto', valor: u => nombreRol(u['Rol']) },
                { header: 'Activo', key: 'activo', tipo: 'booleano', valor: u => u['Activo'] === 'true',
                  resaltar: u => (u['Activo'] === 'true' ? 'ok' : 'alerta') },
                { header: 'Fecha de nacimiento', key: 'nacimiento', tipo: 'fecha', valor: u => u['Fecha Nacimiento'] || null },
                { header: 'Género', key: 'genero', tipo: 'texto', valor: u => capitalizar(u['Género']) },
                { header: 'Comuna', key: 'comuna', tipo: 'texto', valor: u => u['Comuna'] },
                { header: 'Región', key: 'region', tipo: 'texto', valor: u => u['Región'] },
                { header: 'Dirección', key: 'direccion', tipo: 'texto', ancho: 40, valor: u => u['Dirección'] },
                { header: 'Fecha de registro', key: 'creado', tipo: 'fechaHora', valor: u => horaLocal(u['Fecha Creación']) },
                { header: 'Última modificación', key: 'modificado', tipo: 'fechaHora', valor: u => horaLocal(u['Última Modificación']) },
                { header: 'ID usuario', key: 'id', tipo: 'texto', valor: u => u['ID Usuario'] },
            ];
            // La API no siempre llena todos los campos (Región viene vacía): no
            // tiene sentido una columna en blanco de punta a punta.
            const conDatos = columnas.filter(c => usuarios.some(u => {
                const v = c.valor(u);
                return v !== null && v !== undefined && v !== '';
            }));

            await exportarHojaUnica({
                archivo: 'usuarios',
                titulo: 'Usuarios',
                subtitulo: `${usuarios.length.toLocaleString('es-CL')} usuarios registrados`,
                usuario: user,
                filtros: {
                    'Rol': rol ? nombreRol(rol) : 'Todos',
                    'Estado': estado === 'true' ? 'Activos' : estado === 'false' ? 'Inactivos' : 'Todos',
                    'Búsqueda': q.trim() || null,
                },
                nombreHoja: 'Usuarios',
                columnas: conDatos,
                filas: usuarios,
            });
            notify.success('Usuarios exportados exitosamente');
        } catch (error) {
            notify.error(error?.message || 'Error al exportar usuarios');
            console.error('Error exportando CSV:', error);
        } finally {
            setExportandoCSV(false);
        }
    };


    const handleOpenEditModal = (record) => {
        setEditingUser(record);
        setEditModalVisible(true);
        editForm.setFieldsValue({
            role: record.role,
            area: record.area || undefined,
        });
    };

    const handleCloseEditModal = () => {
        setEditModalVisible(false);
        setEditingUser(null);
        editForm.resetFields();
    };

    const handleSaveRole = async (values) => {
        try {
            setEditLoading(true);
            const finalArea = values.role === 'jefe_recinto' ? values.area : 'global';
            const payload = { role: values.role, area: finalArea };

            await withNotify(
                Usuarios.ChangeRol(editingUser.userId, payload),
                { loading: "Actualizando rol...", success: "Rol actualizado correctamente", error: "No se pudo actualizar el rol" }
            );

            setUsers(prev => prev.map(u =>
                u.userId === editingUser.userId ? { ...u, role: values.role, area: finalArea } : u
            ));
            handleCloseEditModal();
        } catch (error) {
            console.error('Error updating role:', error);
        } finally {
            setEditLoading(false);
        }
    };

    const loadFamiliares = async (userId) => {
        try {
            setFamiliaresLoading(true);
            const res = await Familiares.ObtenerPorUsuario(userId);
            setFamiliares(res || []);
        } catch (e) {
            setFamiliares([]);
        } finally {
            setFamiliaresLoading(false);
        }
    };

    const handleDesvincularFamiliar = (familiar) => {
        modal.confirm({
            title: '¿Desvincular familiar?',
            content: (
                <div>
                    <p>Se eliminará permanentemente a <strong>{familiar.nombre}</strong> (RUT: {familiar.rut}) de esta cuenta.</p>
                    <p className="text-gray-500 text-sm mt-1">El familiar podrá ser asignado a otra cuenta posteriormente.</p>
                </div>
            ),
            okText: 'Desvincular',
            okButtonProps: { danger: true },
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await Familiares.AdminDelete(familiar._id);
                    setFamiliares(prev => prev.filter(f => f._id !== familiar._id));
                    notify.success(`Familiar "${familiar.nombre}" desvinculado correctamente`);
                } catch (e) {
                    notify.error(e?.error || 'No se pudo desvincular el familiar');
                }
            }
        });
    };

    const handleOpenEditFullModal = (record) => {
        setEditingUser(record);
        setTabEdicion("datos");
        setEditFullModalVisible(true);
        loadFamiliares(record.userId);
        editFullForm.setFieldsValue({
            name: record.name,
            email: record.email || record.cognitoData?.attributes?.email,
            phone_number: record.phone || record.cognitoData?.attributes?.phone_number,
            address: record.address || record.cognitoData?.attributes?.['custom:address'],
            commune: record.commune || record.cognitoData?.attributes?.['custom:commune'],
            gender: (record.gender || record.cognitoData?.attributes?.['custom:gender'] || '').toLowerCase() || undefined,
            birthdate: record.birthdate || record.cognitoData?.attributes?.birthdate,
            enfermedadesBases: record.enfermedadesBases || record.cognitoData?.attributes?.['custom:enfermedadesBases'],
            role: record.role,
            area: record.area || 'global',
            rut: record.rut,
        });
    };

    const handleCloseEditFullModal = () => {
        setEditFullModalVisible(false);
        setEditingUser(null);
        editFullForm.resetFields();
        setFamiliares([]);
    };

    const handleSaveFullEdit = async (values) => {
        try {
            setEditFullLoading(true);
            const finalArea = values.role === 'jefe_recinto' ? values.area : 'global';
            const payload = { ...values, area: finalArea };

            await withNotify(
                Usuarios.Update(editingUser.userId, payload),
                { loading: "Actualizando usuario...", success: "Usuario actualizado correctamente", error: "No se pudo actualizar el usuario" }
            );

            setUsers(prev => prev.map(u =>
                u.userId === editingUser.userId
                    ? { ...u, ...values, area: finalArea, phone: values.phone_number, email: values.email }
                    : u
            ));
            handleCloseEditFullModal();
        } catch (error) {
            console.error('Error updating user:', error);
        } finally {
            setEditFullLoading(false);
        }
    };


    const toggleEstado = async (r) => {
        const activo = r.isActive === true || r.isActive === "true";
        setUsers(prev => prev.map(x => x.userId === r.userId ? { ...x, isActive: !activo } : x));
        try {
            await withNotify(
                Usuarios.ToggleStatus(r.userId, { enable: !activo }),
                activo
                    ? { loading: "Desactivando...", success: "Usuario desactivado", error: "No se pudo desactivar" }
                    : { loading: "Activando...", success: "Usuario activado", error: "No se pudo activar" }
            );
        } catch {
            setUsers(prev => prev.map(x => x.userId === r.userId ? { ...x, isActive: activo } : x));
        }
    };

    const confirmarBorrado = (r) => {
        modal.confirm({
            title: "¿Eliminar este usuario?",
            content: `Se eliminará la cuenta de ${formatearNombre(r.name) || r.email} (${r.email}). Esta acción no se puede deshacer.`,
            okText: "Eliminar", okType: "danger", cancelText: "Cancelar",
            onOk: async () => {
                const prevUsers = users;
                setUsers(prev => prev.filter(x => x.userId !== r.userId));
                setTotalUsers(prev => prev - 1);
                try {
                    await withNotify(
                        Usuarios.Delete(r.userId),
                        { loading: "Eliminando usuario...", success: "Usuario eliminado", error: "No se pudo eliminar" }
                    );
                } catch {
                    setUsers(prevUsers);
                    setTotalUsers(prev => prev + 1);
                }
            },
        });
    };

    const columns = [
        {
            title: "Usuario",
            key: "usuario",
            render: (_, r) => {
                const esYo = r.userId === user?.sub;
                return (
                    <div className="flex items-center gap-3 min-w-0">
                        <Iniciales nombre={r.name} />
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-900 truncate">{formatearNombre(r.name) || "Sin nombre"}</span>
                                {esYo && <span className="px-2 py-0.5 rounded-full bg-secondary text-[#00266b] text-[10px] font-extrabold uppercase">Tú</span>}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                                {r.email}{r.rut && <span className="text-gray-400"> · {r.rut}</span>}
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Rol",
            dataIndex: "role",
            width: 190,
            render: (role, record) => {
                // El nombre visible sale de la lista de roles, no de un mapa fijo: si
                // no, un rol nuevo se vería como "No Administrativo" en la tabla.
                const nombreRol = NOMBRES_ROL[role] || (role && role !== 'cliente' ? role : "Vecino");
                const esStaff = role && role !== 'cliente';
                return (
                    <div>
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold capitalize
                            ${esStaff ? 'bg-secondary/40 text-[#5c4300]' : 'bg-gray-100 text-gray-600'}`}>
                            {nombreRol}
                        </span>
                        {record.area && record.area !== 'global' && (
                            <div className="text-xs text-gray-500 mt-1 truncate max-w-[170px]">{record.area}</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Estado",
            dataIndex: "isActive",
            width: 150,
            render: (v) => <ChipEstado activo={v === true || v === "true"} labelInactivo="Deshabilitado" />,
        },
        {
            title: "Último acceso",
            dataIndex: "lastLogin",
            width: 160,
            render: (v) => v ? (
                <Tooltip title={dayjs(v).format("DD/MM/YYYY HH:mm")}>
                    <span className="text-sm text-gray-700 cursor-help">{dayjs(v).fromNow()}</span>
                </Tooltip>
            ) : <span className="text-sm text-gray-400">Nunca</span>,
        },
        {
            title: <span className="sr-only">Acciones</span>,
            key: "acciones",
            width: 150,
            align: "right",
            render: (_, r) => {
                const activo = r.isActive === true || r.isActive === "true";
                const isCurrentUser = r.userId === user?.sub;
                // Lo peligroso (borrar) queda al final del menú, en rojo y separado.
                const items = isCurrentUser ? [] : [
                    user?.isAdmin && { key: "editar", icon: <EditOutlined />, label: "Editar datos", onClick: () => handleOpenEditFullModal(r) },
                    canManageUsers && { key: "rol", icon: <UserSwitchOutlined />, label: "Cambiar rol y área", onClick: () => handleOpenEditModal(r) },
                    canManageUsers && {
                        key: "estado",
                        icon: activo ? <StopOutlined /> : <CheckCircleOutlined />,
                        label: activo ? "Deshabilitar cuenta" : "Habilitar cuenta",
                        onClick: () => toggleEstado(r),
                    },
                    user?.isAdmin && { type: "divider" },
                    user?.isAdmin && { key: "borrar", icon: <DeleteOutlined />, label: "Eliminar usuario", danger: true, onClick: () => confirmarBorrado(r) },
                ].filter(Boolean);

                return (
                    <div className="inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={() => setFichaUsuario(r)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-700 hover:border-primary hover:text-primary transition-colors">
                            <FiFileText size={13} /> Ficha
                        </button>
                        {items.length > 0 && (
                            <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }}>
                                <button type="button" aria-label={`Más acciones para ${r.name || r.email}`}
                                    className="w-8 h-8 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
                                    <FiMoreVertical size={16} />
                                </button>
                            </Dropdown>
                        )}
                    </div>
                );
            },
        }
    ];

    const hayFiltros = rol || estado || q;

    return (
        <div>
            <EncabezadoAdmin
                titulo={canManageUsers ? 'Gestión de' : 'Consulta de'}
                destacado="usuarios"
                subtitulo={`${totalUsers.toLocaleString('es-CL')} ${hayFiltros ? 'resultados con los filtros activos' : 'usuarios registrados'}`}
            >
                {canManageUsers && (
                    <Tooltip title={hayFiltros ? `Exporta los ${totalUsers.toLocaleString('es-CL')} resultados filtrados` : `Exporta todos (${totalUsers.toLocaleString('es-CL')})`}>
                        <button type="button" onClick={handleExportarCSV} disabled={totalUsers === 0 || exportandoCSV} className={claseBtnSecundario}>
                            <FiDownload size={15} className={exportandoCSV ? 'animate-bounce' : ''} /> {exportandoCSV ? 'Exportando…' : 'Exportar Excel'}
                        </button>
                    </Tooltip>
                )}
                {canManageUsers && (
                    <button type="button" onClick={() => navigate("/admin/usuarios/crear")} className={claseBtnPrimario}>
                        <FiPlus size={16} /> Nuevo usuario
                    </button>
                )}
            </EncabezadoAdmin>

            {/* ── Filtros ── */}
            <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
                <Input
                    allowClear
                    prefix={<FiSearch className="text-gray-400" />}
                    placeholder="Buscar por nombre, email o RUT…"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onPressEnter={() => handleSearch(qInput)}
                    className="!rounded-full"
                    style={{ maxWidth: 340 }}
                    aria-label="Buscar usuarios"
                />
                <FiltroChips
                    label="Estado de la cuenta"
                    valor={estado}
                    onChange={(v) => { setEstado(v); setPage(1); }}
                    opciones={[
                        { value: "", label: "Todos" },
                        { value: "true", label: "Activos" },
                        { value: "false", label: "Deshabilitados" },
                    ]}
                />
                <Select
                    value={rol}
                    onChange={(value) => { setRol(value); setPage(1); }}
                    options={ROLES_OPTIONS}
                    style={{ minWidth: 200 }}
                    aria-label="Rol"
                />
                {hayFiltros && (
                    <button type="button" onClick={handleClearFilters} className="text-sm font-semibold text-gray-500 hover:text-primary">
                        Limpiar filtros
                    </button>
                )}
            </div>

            {/* ── Tabla ── */}
            <div className="admin-table-card">
                <Table
                    loading={loading}
                    dataSource={users}
                    columns={columns}
                    rowKey="userId"
                    pagination={false}
                    sticky
                    scroll={{ x: 900 }}
                    rowClassName={(record) => `fila-clicable ${record.userId === user?.sub ? "fila-yo" : ""}`}
                    onRow={(record) => ({ onClick: () => setFichaUsuario(record) })}
                    locale={{
                        emptyText: (
                            <div className="py-10 text-center">
                                <p className="font-bold text-gray-900 m-0">No encontramos usuarios</p>
                                <p className="text-sm text-gray-500 m-0 mt-1">
                                    {hayFiltros ? 'Prueba con otra búsqueda o limpia los filtros.' : 'Aún no hay usuarios registrados.'}
                                </p>
                            </div>
                        ),
                    }}
                />
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
                    <span className="text-sm text-gray-500">
                        Mostrando <strong className="text-gray-900">{users.length}</strong> de <strong className="text-gray-900">{totalUsers.toLocaleString('es-CL')}</strong>
                    </span>
                    <Space>
                        <Select
                            value={limit}
                            onChange={(v) => { setLimit(v); setPage(1); }}
                            options={[10, 20, 50, 100].map(n => ({ label: `${n} / pág`, value: n }))}
                            style={{ width: 120 }}
                            aria-label="Usuarios por página"
                        />
                        <Pagination
                            current={page}
                            total={totalUsers}
                            pageSize={limit}
                            onChange={setPage}
                            showSizeChanger={false}
                            size="small"
                        />
                    </Space>
                </div>
            </div>



            {/* ── Modal edición de rol ── */}
            <Modal
                title={<div className="flex items-center gap-2"><UserSwitchOutlined className="text-primary" /><span>Editar Rol y Área</span></div>}
                open={editModalVisible}
                onCancel={handleCloseEditModal}
                onOk={() => editForm.submit()}
                confirmLoading={editLoading}
                okText="Guardar Cambios"
                cancelText="Cancelar"
                width={500}
            >
                {editingUser && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-600">Usuario:</div>
                        <div className="font-semibold text-lg">{editingUser.name}</div>
                        <div className="text-sm text-gray-500">{editingUser.email}</div>
                    </div>
                )}
                <Form form={editForm} layout="vertical" onFinish={handleSaveRole}>
                    <Form.Item name="role" label={<span className="font-semibold">Rol</span>} rules={[{ required: true, message: 'Selecciona un rol' }]}>
                        <Select size="large" options={ROLES_EDIT_OPTIONS} placeholder="Selecciona un rol" />
                    </Form.Item>
                    {selectedRole === 'jefe_recinto' && (
                        <Form.Item name="area" label={<span className="font-semibold">Área / Recinto</span>} rules={[{ required: true, message: 'Selecciona un recinto' }]}>
                            <Select size="large" options={recintos} placeholder="Selecciona un recinto" loading={loadingRecintos} />
                        </Form.Item>
                    )}
                </Form>
            </Modal>


            {/* ── Modal edición completa ──
                En pestañas y con los datos en tres columnas: antes era una sola
                columna larga (datos + rol + familiares) que obligaba a hacer scroll. */}
            <Modal
                open={editFullModalVisible}
                onCancel={handleCloseEditFullModal}
                onOk={() => editFullForm.submit()}
                confirmLoading={editFullLoading}
                okText="Guardar cambios"
                cancelText="Cancelar"
                width={880}
                centered
                title={editingUser && (
                    <div className="flex items-center gap-3 pr-8">
                        <Iniciales nombre={editingUser.name} className="!w-10 !h-10" />
                        <div className="min-w-0">
                            <div className="text-base font-extrabold text-gray-900 truncate">
                                Editar a {formatearNombre(editingUser.name) || 'usuario'}
                            </div>
                            <div className="text-xs font-normal text-gray-500 truncate">{editingUser.email}</div>
                        </div>
                    </div>
                )}
                styles={{ body: { paddingTop: 4 } }}
            >
                <Form
                    form={editFullForm}
                    layout="vertical"
                    onFinish={handleSaveFullEdit}
                    className="form-compacto"
                    // Si falla un campo de otra pestaña, se salta a ella para que se vea el error.
                    onFinishFailed={({ errorFields }) => {
                        const campo = errorFields?.[0]?.name?.[0];
                        setTabEdicion(['role', 'area'].includes(campo) ? 'acceso' : 'datos');
                    }}
                >
                    <Tabs
                        activeKey={tabEdicion}
                        onChange={setTabEdicion}
                        items={[
                            {
                                key: 'datos',
                                label: 'Datos personales',
                                forceRender: true,
                                children: (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                                        <Form.Item name="name" label="Nombre completo" className="sm:col-span-2" rules={[{ required: true, message: 'Ingresa el nombre' }]}>
                                            <Input placeholder="Juan Pérez" />
                                        </Form.Item>
                                        <Form.Item name="rut" label="RUT" rules={[{ required: true, message: 'Ingresa el RUT' }]}>
                                            <Input placeholder="12.345.678-9" />
                                        </Form.Item>
                                        <Form.Item name="email" label="Correo electrónico" className="sm:col-span-2"
                                            rules={[{ required: true, message: 'Ingresa el email' }, { type: 'email', message: 'Ingresa un email válido' }]}>
                                            <Input placeholder="usuario@ejemplo.com" />
                                        </Form.Item>
                                        <Form.Item name="phone_number" label="Teléfono" rules={[{ required: true, message: 'Ingresa el teléfono' }]}>
                                            <Input placeholder="+56912345678" />
                                        </Form.Item>
                                        <Form.Item name="birthdate" label="Fecha de nacimiento">
                                            <Input type="date" />
                                        </Form.Item>
                                        <Form.Item name="gender" label="Género">
                                            <Select options={GENDER_OPTIONS} placeholder="Selecciona" />
                                        </Form.Item>
                                        <Form.Item name="commune" label="Comuna">
                                            <Select options={COMUNAS_OPTIONS} placeholder="Selecciona" showSearch />
                                        </Form.Item>
                                        <Form.Item name="address" label="Dirección" className="sm:col-span-3">
                                            <Input placeholder="Calle Principal 123, Depto 456" />
                                        </Form.Item>
                                        <Form.Item name="enfermedadesBases" label="Enfermedades de base" className="sm:col-span-3 !mb-0">
                                            <Input.TextArea placeholder="Ninguna / Diabetes, hipertensión, etc." autoSize={{ minRows: 1, maxRows: 3 }} />
                                        </Form.Item>
                                    </div>
                                ),
                            },
                            {
                                key: 'acceso',
                                label: 'Rol y acceso',
                                forceRender: true,
                                children: (
                                    <div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                            <Form.Item name="role" label="Rol" rules={[{ required: true, message: 'Selecciona un rol' }]}>
                                                <Select options={ROLES_EDIT_OPTIONS} placeholder="Selecciona un rol" />
                                            </Form.Item>
                                            {selectedFullRole === 'jefe_recinto' && (
                                                <Form.Item name="area" label="Área / Recinto" rules={[{ required: true, message: 'Selecciona un recinto' }]}>
                                                    <Select options={recintos} placeholder="Selecciona un recinto" loading={loadingRecintos} showSearch optionFilterProp="label" />
                                                </Form.Item>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 m-0">
                                            Los permisos de cada rol se configuran en <strong>Roles y permisos</strong>. Jefe de recinto necesita además el recinto que administra.
                                        </p>
                                    </div>
                                ),
                            },
                            {
                                key: 'familiares',
                                label: (
                                    <span className="inline-flex items-center gap-2">
                                        Familiares
                                        {!familiaresLoading && familiares.length > 0 && (
                                            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold inline-flex items-center justify-center">{familiares.length}</span>
                                        )}
                                    </span>
                                ),
                                children: familiaresLoading ? (
                                    <div className="py-8 text-center text-sm text-gray-400">Cargando familiares…</div>
                                ) : familiares.length === 0 ? (
                                    <div className="py-8 text-center text-sm text-gray-500">Este usuario no tiene familiares vinculados.</div>
                                ) : (
                                    <ul className="m-0 p-0 list-none grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto scroll-fino pr-1">
                                        {familiares.map(f => {
                                            const edad = f.fechaNacimiento ? dayjs().diff(dayjs(f.fechaNacimiento), 'year') : null;
                                            const tipoEdad = edad === null ? null : edad < 18 ? 'Menor' : edad >= 60 ? 'Adulto mayor' : 'Adulto';
                                            return (
                                                <li key={f._id} className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100 bg-white">
                                                    <Iniciales nombre={f.nombre} />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-bold text-gray-900 truncate">{formatearNombre(f.nombre)}</div>
                                                        <div className="text-xs text-gray-500 truncate">
                                                            <span className="font-mono">{f.rut}</span>
                                                            {tipoEdad && <> · {tipoEdad}, {edad} años</>}
                                                        </div>
                                                    </div>
                                                    <Tooltip title="Desvincular (eliminación permanente)">
                                                        <button type="button" onClick={() => handleDesvincularFamiliar(f)}
                                                            aria-label={`Desvincular a ${f.nombre}`}
                                                            className="w-8 h-8 flex-shrink-0 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                                                            <DeleteOutlined />
                                                        </button>
                                                    </Tooltip>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ),
                            },
                        ]}
                    />
                </Form>
            </Modal>


            {/* ── Ficha de usuario ── */}
            <FichaUsuarioDrawer
                usuario={fichaUsuario}
                onClose={() => setFichaUsuario(null)}
            />
        </div>
    );
}
